import styles from 'css/src/components/sketch/SketchDropZone.module.scss';

import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';

import { Toast } from 'js/src/components/common/Toast';
import { SupportedFileType } from 'js/src/components/sketch/FileUpload';

interface SketchDropZoneProps {
    onFilesDropped: (files: File[]) => void;
    disabled?: boolean;
    children: React.ReactNode;
}

const SUPPORTED_FILE_TYPES = Object.values(SupportedFileType);

export const SketchDropZone: React.FC<SketchDropZoneProps> = ({
    onFilesDropped,
    disabled = false,
    children,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const disableMouseEvents = useCallback(() => {
        if (wrapperRef.current === null) {
            return;
        }
        const children = wrapperRef.current.children;
        for (const child of children) {
            child.classList.add('pe-none');
        }
    }, []);

    const enableMouseEvents = useCallback(() => {
        if (wrapperRef.current === null) {
            return;
        }
        const children = wrapperRef.current.children;
        for (const child of children) {
            child.classList.remove('pe-none');
        }
    }, []);

    const handleDragEnter = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (disabled) return;

            // Disable mouse events on child elements to prevent drag event conflicts
            disableMouseEvents();

            // Only set dragging if the event target is the wrapper itself
            if (e.target === wrapperRef.current) {
                setIsDragging(true);
            }
        },
        [disabled, disableMouseEvents],
    );

    const handleDragLeave = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (disabled) return;

            // Only handle drag leave events from the wrapper itself
            if (e.target !== wrapperRef.current) {
                return;
            }

            enableMouseEvents();
            setIsDragging(false);
        },
        [disabled, enableMouseEvents],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (disabled) return;

            // Reset UI state
            enableMouseEvents();
            setIsDragging(false);

            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                // Filter for supported file types
                const supportedFiles = files.filter((file) =>
                    SUPPORTED_FILE_TYPES.includes(file.type as SupportedFileType),
                );
                const unsupportedFiles = files.filter(
                    (file) => !SUPPORTED_FILE_TYPES.includes(file.type as SupportedFileType),
                );

                if (unsupportedFiles.length > 0) {
                    Toast.error(
                        'Unsupported file types detected. Please upload documents (PDF, DOCX, PPTX, TXT, XML) or media files (images and videos).',
                    );
                }

                if (supportedFiles.length > 0) {
                    onFilesDropped(supportedFiles);
                }
            }
        },
        [disabled, enableMouseEvents, onFilesDropped],
    );

    return (
        <div
            ref={wrapperRef}
            className={classNames(styles['sketch-drop-zone'], {
                [styles['dragging']]: isDragging,
            })}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ position: 'relative', padding: '1px' }}
        >
            {isDragging && (
                <div className={styles['dragging-overlay']}>
                    <i translate="no" className="material-symbols-outlined">
                        cloud_upload
                    </i>
                    <div>Drop your files here</div>
                    <div className={`${styles['small']} text-muted`}>
                        Documents (PDF, DOCX, PPTX, TXT, XML) and media (images, videos) supported
                    </div>
                </div>
            )}
            {children}
        </div>
    );
};
